// 功能權限設定 modal（role.jsp `ruleSettingModal`，455-480 行附近）。
//
// legacy 流程（role.jsp:293-357）：
//   「角色設定」連結 → `config/getRolePermissionListAjax {id}` 取該角色已勾權限 →
//   modal 內以全站權限目錄（gePermissionListAjax）畫 checkbox 表 →
//   「模組查詢」下拉過濾表格（column(0).search）→「存檔」→
//   `config/saveRolePermissionAjax {roleId, permissionIds[]}`。
//
// 雛形：目錄＝`role-permission-catalog.ts` 種子；該角色已勾集合存在 row 的
// `permissionIds`（resource-rules 種子＋localStorage 持久化）；存檔走既有
// `dataSource.update('config-role', id, { permissionIds })`（D-1 五個 op 之內，
// criteria C5-j：真的呼叫 DataSource 的按鈕才標 data-effect）。
//
// 【證據等級】§7 第 3 級（原始碼，role.jsp 行號見上）。【雛形值待實機校準】

import { useMemo, useState, type ReactElement } from 'react';
import { Button } from '@/components/base/Button';
import { Select } from '@/components/base/Select';
import { ModalFrame, useDialogs } from '@/components/layouts';
import { Table, type TableColumn } from '@/components/base/Table';
import { t } from '@/messages/zh-TW';
import { MockDataError, useDataSource } from '@/mocks';
import type { Row } from '@/mocks/types';
import {
  permissionCellId,
  permissionDisplayName,
  ROLE_PERMISSION_CATALOG,
  ROLE_PERMISSION_KINDS,
} from './role-permission-catalog';

export interface RolePermissionModalProps {
  readonly pageId: string;
  /** 被設定的角色列（權限設定欄「角色設定」連結所在那一列）。 */
  readonly row: Row;
  readonly onClose: () => void;
  /** 存檔成功後回呼（呼叫端 reload 列表）。 */
  readonly onSaved: () => void;
}

export function RolePermissionModal({
  pageId,
  row,
  onClose,
  onSaved,
}: RolePermissionModalProps): ReactElement {
  const dataSource = useDataSource();
  const dialogs = useDialogs();
  const roleName = String(row['category'] ?? '');
  /** 該角色已勾選的權限 id（row.permissionIds；種子與存檔都以字串陣列存）。 */
  const seedChecked = useMemo<ReadonlySet<string>>(() => {
    const raw = row['permissionIds'];
    return new Set(Array.isArray(raw) ? raw.map((id) => String(id)) : []);
  }, [row]);
  const [checked, setChecked] = useState<ReadonlySet<string>>(seedChecked);
  const [groupFilter, setGroupFilter] = useState('');
  const [busy, setBusy] = useState(false);

  const groups = useMemo(
    () => [...new Set(ROLE_PERMISSION_CATALOG.map((entry) => entry.group))],
    [],
  );
  const visible = useMemo(
    () => (groupFilter === '' ? ROLE_PERMISSION_CATALOG : ROLE_PERMISSION_CATALOG.filter((entry) => entry.group === groupFilter)),
    [groupFilter],
  );

  const toggleCell = (cellId: string) => {
    setChecked((current) => {
      const next = new Set(current);
      if (next.has(cellId)) {
        next.delete(cellId);
      } else {
        next.add(cellId);
      }
      return next;
    });
  };

  const columns: readonly TableColumn[] = [
    { key: 'group', header: '模組' },
    { key: 'name', header: '程式名稱' },
    ...ROLE_PERMISSION_KINDS.map((kind) => ({ key: kind.key, header: kind.label })),
  ];

  const save = () => {
    void (async () => {
      setBusy(true);
      try {
        await dialogs.processing.wrap(
          dataSource.update('config-role', String(row.id ?? ''), {
            permissionIds: [...checked],
          }),
        );
        onSaved();
      } catch (cause: unknown) {
        await dialogs.alert(cause instanceof MockDataError ? cause.message : t('error.generic'));
      } finally {
        setBusy(false);
      }
    })();
  };

  return (
    <ModalFrame
      uiId={`${pageId}-permission-dialog`}
      open
      title="功能權限設定"
      size="lg"
      onClose={onClose}
      footer={
        <>
          <Button
            uiId={`${pageId}-permission-dialog-cancel`}
            variant="default"
            size="sm"
            type="button"
            onClick={onClose}
          >
            {t('action.close')}
          </Button>
          <Button
            uiId={`${pageId}-permission-dialog-save`}
            variant="primary"
            size="sm"
            type="button"
            disabled={busy}
            // C5-j：存檔真的呼叫 dataSource.update('config-role', …)。
            dataEffect={`update:${pageId}`}
            onClick={save}
          >
            {t('action.save')}
          </Button>
        </>
      }
    >
      <div data-ui-id={`${pageId}-permission-form`} className="space-y-3">
        <div data-ui-id={`${pageId}-permission-form-name`} className="text-sm">
          角色名稱：{roleName}
        </div>
        <div className="flex items-center gap-2">
          {/* role.jsp:305-315 模組查詢下拉：過濾權限表第 1 欄（模組）。 */}
          <label className="text-sm" htmlFor={`${pageId}-permission-group-select`}>
            模組查詢：
          </label>
          <Select
            uiId={`${pageId}-permission-group-select`}
            value={groupFilter}
            onChange={setGroupFilter}
            options={[{ value: '', label: '全部' }, ...groups.map((group) => ({ value: group, label: group }))]}
          />
        </div>
        <Table
          uiId={`${pageId}-permission-table`}
          columns={columns}
          rows={visible.map((entry) => ({
            id: entry.id,
            group: entry.group,
            name: permissionDisplayName(entry.name, roleName),
            ...Object.fromEntries(
              ROLE_PERMISSION_KINDS.map((kind) => [
                kind.key,
                {
                  cellId: permissionCellId(entry, kind.key),
                  checked: checked.has(permissionCellId(entry, kind.key)),
                  entry,
                  kind: kind.key,
                },
              ]),
            ),
          }))}
          rowKey={(row) => String(row['id'])}
          emptyText="查無符合的資料"
          renderCell={(row, columnKey) => {
            if (columnKey === 'group' || columnKey === 'name') {
              return String(row[columnKey] ?? '');
            }
            const cell = row[columnKey] as
              | { cellId: string; checked: boolean; entry: { name: string }; kind: string }
              | undefined;
            if (cell === undefined) {
              return '\u00a0';
            }
            return (
              <label className="inline-flex items-center gap-1 text-sm">
                <input
                  type="checkbox"
                  data-ui-id={`${pageId}-permission-check-${toKebab(cell.entry.name)}-${cell.kind.toLowerCase()}`}
                  checked={cell.checked}
                  onChange={() => {
                    toggleCell(cell.cellId);
                  }}
                />
                {checkboxText(cell.kind)}
              </label>
            );
          }}
        />
      </div>
    </ModalFrame>
  );
}

/** role.jsp permissionColumn 的 checkbox 後綴文字：查詢／編輯／刪除／匯出。 */
function checkboxText(kindKey: string): string {
  switch (kindKey) {
    case 'ACCESS':
      return '查詢';
    case 'UPDATE':
      return '編輯';
    case 'DELETE':
      return '刪除';
    case 'EXPORT':
      return '匯出';
    default:
      return '';
  }
}

/** name（中文）轉 data-ui-id 用的 kebab：這裡直接用原字串（中文在 id 中是可用的）。 */
function toKebab(value: string): string {
  return value.replace(/\s+/g, '-');
}
